/**
 * Announcement reactions: one slot per employee per post, from the fixed quick set.
 *
 * The server sends a bounded SUMMARY rather than the raw slots —
 * `[{emoji, count, mine, names, more}]` — so a 200-person clinic costs the feed the same as a
 * two-person one, and `mine` arrives already resolved for the caller (the `canEdit`
 * precedent: the server says what you hold, the client just renders it).
 *
 * These are pure functions over that summary, kept out of the components so the toggle rule —
 * the part with real edge cases — is unit-testable.
 */

import { REACTION_QUICK_SET } from "@/lib/messages/reactions-helpers";

// Re-exported so the dashboard never reaches into the messages module for the emoji list;
// there is exactly one source of truth, and it matches the backend allow-list byte for byte.
export { REACTION_QUICK_SET };

const QUICK_SET_ORDER = new Map(REACTION_QUICK_SET.map((emoji, index) => [emoji, index]));

/**
 * Coerce a server (or optimistically patched) reaction summary into a clean array. Tolerates
 * absence, a non-array, malformed entries and emoji outside the quick set — the feed must
 * render even if a future server tightens the set.
 */
export function normalizeReactionSummary(value) {
  if (!Array.isArray(value)) return [];
  const out = [];
  for (const entry of value) {
    const emoji = String(entry?.emoji || "").trim();
    if (!QUICK_SET_ORDER.has(emoji)) continue;
    const count = Number(entry?.count);
    if (!Number.isFinite(count) || count <= 0) continue;
    const more = Number(entry?.more);
    out.push({
      emoji,
      count: Math.trunc(count),
      mine: entry?.mine === true,
      names: Array.isArray(entry?.names)
        ? entry.names.map((name) => String(name || "").trim()).filter(Boolean)
        : [],
      more: Number.isFinite(more) && more > 0 ? Math.trunc(more) : 0,
    });
  }
  return out.sort((a, b) => QUICK_SET_ORDER.get(a.emoji) - QUICK_SET_ORDER.get(b.emoji));
}

/** The emoji the caller currently holds on this post, or "" when they haven't reacted. */
export function myReactionEmoji(summary) {
  return normalizeReactionSummary(summary).find((entry) => entry.mine)?.emoji || "";
}

/**
 * What a click on `emoji` should do: clicking the emoji you already hold clears it, anything
 * else sets (and therefore replaces) it. One slot per person, so there is no third case.
 */
export function planReactionToggle(summary, emoji) {
  const next = String(emoji || "").trim();
  if (!QUICK_SET_ORDER.has(next)) return null;
  return myReactionEmoji(summary) === next ? { action: "clear" } : { action: "set", emoji: next };
}

/**
 * The summary as it will look once the caller's click lands — rendered immediately, then
 * reconciled from the server's response.
 *
 * The fiddly parts, and why they have tests: moving your reaction has to decrement the old
 * emoji AND drop its pill entirely when you were the last one holding it, while your name
 * leaves the old name list and joins the new one.
 */
export function applyReactionToggle(summary, emoji, myName = "") {
  const plan = planReactionToggle(summary, emoji);
  if (!plan) return normalizeReactionSummary(summary);

  const name = String(myName || "").trim();
  const current = normalizeReactionSummary(summary);
  const withoutMine = [];
  for (const entry of current) {
    if (!entry.mine) {
      withoutMine.push(entry);
      continue;
    }
    // I was in this pill: drop it entirely if I was the only one, otherwise decrement and
    // take my name back out (or shrink the "+N others" tail if I was inside it).
    if (entry.count <= 1) continue;
    const names = entry.names.filter((value) => value !== name);
    withoutMine.push({
      ...entry,
      count: entry.count - 1,
      mine: false,
      names,
      more: names.length === entry.names.length && entry.more > 0 ? entry.more - 1 : entry.more,
    });
  }
  if (plan.action === "clear") return withoutMine;

  const existing = withoutMine.find((entry) => entry.emoji === plan.emoji);
  if (existing) {
    existing.count += 1;
    existing.mine = true;
    if (name) existing.names = [...existing.names, name];
    else existing.more += 1;
  } else {
    withoutMine.push({
      emoji: plan.emoji,
      count: 1,
      mine: true,
      names: name ? [name] : [],
      more: name ? 0 : 1,
    });
  }
  return withoutMine.sort((a, b) => QUICK_SET_ORDER.get(a.emoji) - QUICK_SET_ORDER.get(b.emoji));
}

/** Tooltip text for one pill: who reacted, with a "+N others" tail once the names run out. */
export function reactionTooltip(entry) {
  const names = Array.isArray(entry?.names) ? entry.names.filter(Boolean) : [];
  const more = Number(entry?.more) > 0 ? Math.trunc(entry.more) : 0;
  if (names.length === 0) {
    const count = Number(entry?.count) || more;
    return count === 1 ? "1 person" : `${count} people`;
  }
  if (more === 0) return names.join(", ");
  return `${names.join(", ")} +${more} ${more === 1 ? "other" : "others"}`;
}
